import auth from '@/services/auth'
import i18n from '@/plugins/i18n'

const config = { root: true }

const data = {
  error: null,
  title: null
}

/**
 * ===============================================
 * HTTP Exception Store
 * ===============================================
 *
 * Handles HTTP exceptions by prompting users.
 *
 * ===============================================
 */
export default {
  namespaced: true,
  state () {
    return {
      ...data
    }
  },
  mutations: {
    /**
     * Reset the state to default.
     *
     * @param {*} state
     */
    RESET (state) {
      for (const key in state) {
        state[key] = data[key]
      }
    },

    /**
     * Set the state error.
     *
     * @param {*} state
     * @param {*} error
     */
    SET_ERROR (state, error) {
      state.error = error
    },

    /**
     * Set the state error.
     *
     * @param {*} state
     * @param {*} error
     */
    SET_TITLE (state, title) {
      state.title = title
    }
  },
  actions: {
    /**
     * Determine the appropriate action for the error
     *
     * @param error
     */
    handle ({ commit, dispatch }, error) {
      if (!error) return dispatch('criticalError')

      commit('SET_ERROR', error)
      commit('SET_TITLE', !error.title ? null : error.title)

      switch (error.status) {
        case 400:
          return dispatch('badRequest')

        case 401:
          return dispatch('unauthenticated')

        case 403:
          return dispatch('unauthorized')

        case 404:
          return dispatch('notFound')

        case 422:
          return dispatch('unprocessable')

        case 429:
          return dispatch('tooManyRequestsError')

        case 500:
          return dispatch('internalServerError')

        default:
          return dispatch('defaultError')
      }
    },

    /**
     * Shows an alert dialog with a status of 400.
     *
     */
    badRequest ({ commit, state }) {
      const data = {
        title: i18n.t('exception.unprocessable'),
        body: state.error.data.message
      }

      if ('showDialog' in state.error) return

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Handles critical error without response.
     *
     */
    criticalError ({ commit }) {
      const data = {
        title: i18n.t('exception.criticalError.title'),
        body: i18n.t('exception.criticalError.body')
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with the error status from response.
     *
     */
    defaultError ({ commit, state }) {
      const data = {
        title: state.error.statusText,
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 500.
     *
     */
    internalServerError ({ commit, state }) {
      const data = {
        title: i18n.t('exception.internalServerError'),
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 404.
     *
     */
    notFound ({ commit, state }) {
      const data = {
        title: i18n.t('exception.notFound'),
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 401.
     *
     */
    unauthenticated ({ commit, state }) {
      const data = {
        title: i18n.t('exception.unauthenticated'),
        body: state.error.data.message,
        action: () => {
          auth.flush()
          window.location.replace(`${window.location.origin}/login`)
        }
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 403.
     *
     */
    unauthorized ({ commit, state }) {
      const data = {
        title: !state.title ? i18n.t('exception.unauthorized') : state.title,
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 422.
     *
     */
    unprocessable ({ commit, state }) {
      const data = {
        title: i18n.t('exception.unprocessable'),
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    },

    /**
     * Shows an alert dialog with a status of 429.
     *
     */
    tooManyRequestsError ({ commit, state }) {
      const data = {
        title: i18n.t('exception.tooManyRequestsError'),
        body: state.error.data.message
      }

      commit('dialog/OPEN_ALERT_DIALOG', data, config)
    }
  }
}
